import { ChangeEvent, useEffect, useState, FocusEvent, Dispatch, useCallback, MutableRefObject, useMemo } from 'react';
import debounce from 'lodash.debounce';
import { connect, useDispatch } from 'react-redux';
import { FormApi } from 'final-form';
import { FormSpy } from 'react-final-form';

import { AutoComplete } from 'UIPackage/MainModule/components/AutoComplete';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';

import { getErvkOrganisations, setErvkOrganisationsSearchQuery } from '../actions/ChangeKnoInMeasureActions';
import { IKnoOrganisationChild } from '../../APIPackage/CatalogsModule/interfaces/IKnoOrganisationChild';
import { getKnoErvkOrganisations } from '../selectors/changeKnoInMeasureSelectors';
import { IOrganisationAutocompleteOption } from '../../PersonalAccountPackage/AdminModule/interfaces/IOrganisationAutocompleteOption';
import { IChangeKnoInMeasureForm } from '../../PersonalAccountPackage/AdminModule/interfaces/IChangeKnoInMeasureForm';
import { REQUIRED_ERROR } from '../../CorePackage/MainModule/constants/validationErrors';
import { TextInputField } from '../../UIPackage/MainModule/components/TextInputField';
import { getNotices } from '../../CorePackage/NotificationModule/reducers/noticeReducer';
import { INotification } from '../../CorePackage/NotificationModule/interfaces/INotification';

const SEARCH_TEXT_MIN_LENGTH = 5;
const generateInfoText = (knm: IKnoOrganisationChild) => `${knm.fullName ?? ''}`;

interface IErvkKnoOrganisationSearchProps {
  formRef: MutableRefObject<FormApi<IChangeKnoInMeasureForm, Partial<IChangeKnoInMeasureForm>> | undefined>;
}

interface IErvkKnoOrganisationSearchStoreProps {
  ervkKnoOrganisations: IKnoOrganisationChild[];
  notifications: INotification[];
}

const ErvkKnoOrganisationSearchWithoutStore = ({
  formRef,
  ervkKnoOrganisations,
  notifications,
}: IErvkKnoOrganisationSearchProps & IErvkKnoOrganisationSearchStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const [organisationAutocompleteOption, setOrganisationAutocompleteOption] = useState<
    IOrganisationAutocompleteOption[] | undefined
  >();

  const [isSearchTextSelected, setIsSearchTextSelected] = useState(false);
  const [isFounded, setIsFounded] = useState(false);

  const findErvkOrganisations = useCallback(
    (searchText?: string) => {
      if (searchText && searchText.length >= 5) {
        setIsFounded(true);
        dispatch(setErvkOrganisationsSearchQuery(searchText));
        dispatch(getErvkOrganisations());
      }
    },
    [dispatch],
  );

  const debouncedFindErvkOrganisations = useMemo(() => debounce(findErvkOrganisations, 2000), [findErvkOrganisations]);

  useEffect(() => () => debouncedFindErvkOrganisations.cancel(), [debouncedFindErvkOrganisations]);

  const handleTextInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      const searchText = value?.trim();

      if (searchText) {
        formRef?.current?.resetFieldState('ervkKnoOrganizationTitle');
      }

      formRef?.current?.change('ervkKnoOrganization');

      if (!searchText) {
        setIsSearchTextSelected(false);
      }

      debouncedFindErvkOrganisations(searchText);

      if (!searchText || searchText.length <= SEARCH_TEXT_MIN_LENGTH) {
        setOrganisationAutocompleteOption(undefined);
      }
    },
    [debouncedFindErvkOrganisations, formRef],
  );

  const handleAutoCompleteSelect = useCallback(
    (value: string) => () => {
      const currentOption = organisationAutocompleteOption?.find(item => item.value === value);

      formRef?.current?.change('ervkKnoOrganizationTitle', value);
      formRef?.current?.change('ervkKnoOrganization', currentOption?.ervkKnoOrganization);
      formRef?.current?.resetFieldState('ervkKnoOrganizationTitle');

      debouncedFindErvkOrganisations('');
      setOrganisationAutocompleteOption(undefined);
      setIsSearchTextSelected(true);
    },
    [debouncedFindErvkOrganisations, organisationAutocompleteOption, formRef],
  );

  useEffect(() => {
    isFounded &&
      setOrganisationAutocompleteOption(
        ervkKnoOrganisations.map(kno => {
          const knoText = `${generateInfoText(kno)}`;

          return {
            value: knoText,
            label: knoText,
            ervkKnoOrganization: kno,
          };
        }),
      );
  }, [ervkKnoOrganisations, isFounded]);

  const handleTextInputClear = useCallback(() => {
    setIsSearchTextSelected(false);
    formRef?.current?.change('ervkKnoOrganizationTitle');
    formRef?.current?.change('ervkKnoOrganization');
  }, []);

  const handleTextInputBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      const tagName = (e?.relatedTarget as Element)?.tagName;

      if (tagName !== 'UL') {
        debouncedFindErvkOrganisations('');
        setOrganisationAutocompleteOption(undefined);
      }
    },
    [debouncedFindErvkOrganisations, isSearchTextSelected],
  );

  const validateKnoOrganisation = (value: string | undefined, allValues: IChangeKnoInMeasureForm) => {
    if (!value?.trim()) {
      return REQUIRED_ERROR;
    }

    if (!allValues.ervkKnoOrganization) {
      return 'КНО обязательно для выбора';
    }

    return undefined;
  };

  return (
    <div className="shared-row-field">
      <AutoComplete
        options={organisationAutocompleteOption}
        noFoundMessage="КНО соответствующие тексту поиска не найдены"
        handleAutoCompleteSelect={handleAutoCompleteSelect}
      >
        <TextInputField
          id="ervkKnoOrganizationTitle"
          name="ervkKnoOrganizationTitle"
          placeholder="Введите ОГРН или часть наименования КНО"
          clearable={isSearchTextSelected}
          onChange={handleTextInputChange}
          onClear={handleTextInputClear}
          onBlur={handleTextInputBlur}
          validate={validateKnoOrganisation}
        />
        <FormSpy subscription={{ values: true }}>
          {() => {
            if (!isSearchTextSelected) {
              return null;
            }

            const notification = notifications?.find(item => item.messageType === 'GET_KNO_SUPERVISION');

            if (!notification) {
              return null;
            }

            return <p className="text-input-field__helper">{notification.renderContent?.toString()}</p>;
          }}
        </FormSpy>
      </AutoComplete>
    </div>
  );
};

const mapStateToProps = (state: IRootState): IErvkKnoOrganisationSearchStoreProps => ({
  ervkKnoOrganisations: getKnoErvkOrganisations(state),
  notifications: getNotices(state),
});

export const ErvkKnoOrganisationSearch = connect(mapStateToProps)(ErvkKnoOrganisationSearchWithoutStore);
